import type {
  EntryAgeGatePost,
  EntryCheckResponse,
  EntryCookieConsentPost,
} from '@shared/entry';

const BASE = '/api/v1/entry';

interface Envelope<T> {
  sessionState: string;
  success: boolean;
  data: T | null;
  messages: Array<{ severity: string; message: string; details?: Record<string, unknown> }>;
}

export class EntryApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(BASE + path, {
    ...init,
    headers,
    credentials: 'include',
  });
  const body = (await res.json()) as Envelope<T>;
  if (!res.ok || !body.success) {
    const msg = body.messages[0];
    const code = (msg?.details?.code as string | undefined) ?? 'unknown_error';
    throw new EntryApiError(res.status, code, msg?.message ?? 'Request failed', msg?.details);
  }
  if (body.data === null) {
    throw new EntryApiError(res.status, 'empty_data', 'Empty response data');
  }
  return body.data;
}

export const entryApi = {
  check: () => request<EntryCheckResponse>('/check', { method: 'GET' }),

  submitCookieConsent: (body: EntryCookieConsentPost) =>
    request<{
      cookie_consent: { necessary: true; functional: boolean; analytics: boolean; marketing: boolean };
      jurisdiction_tag: string;
    }>('/cookie-consent', { method: 'POST', body: JSON.stringify(body) }),

  submitAgeGate: (body: EntryAgeGatePost) =>
    request<{ age_verified: true; terms_version: string }>('/age-gate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
